import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { cn } from '@crowdex/ui/lib/utils';
import { Check } from 'lucide-react';
import { ArtifactView, WhenVisible } from './artifact-view';

export type ArtifactGridSelection = {
  focusedId?: string | undefined;
  selectedIds: ReadonlySet<string>;
  onFocus(artifact: ArtifactListingEntry): void;
  onToggle(artifact: ArtifactListingEntry): void;
};

/**
 * Artifacts as cards with live previews, loaded while visible. With a selection, a card toggles
 * instead of opening (the composer's reference picker).
 */
export function ArtifactGrid({
  artifacts,
  className,
  onOpen,
  selection,
  size = 'default',
}: {
  artifacts: readonly ArtifactListingEntry[];
  className?: string;
  /** `compact`: small thumbnails under their title alone, as the composer's @ shows them. */
  size?: 'default' | 'compact';
  onOpen?: ((artifact: ArtifactListingEntry) => void) | undefined;
  selection?: ArtifactGridSelection | undefined;
}) {
  return (
    <ul
      className={cn(
        // Two to a row on a phone, with shorter previews: more than three in view.
        size === 'compact'
          ? 'grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2'
          : 'grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-3 max-sm:grid-cols-2 max-sm:gap-2',
        className,
      )}
      data-artifact-library-scroll
    >
      {artifacts.map((artifact) => {
        const selected = selection?.selectedIds.has(artifact.id) ?? false;
        return (
          <li key={artifact.id}>
            <button
              aria-label={artifact.title}
              aria-pressed={selection === undefined ? undefined : selected}
              className={cn(
                'group relative flex w-full flex-col overflow-hidden rounded-xl border bg-surface-raised text-left hover:border-foreground/30',
                selection?.focusedId === artifact.id && 'ring-2 ring-ring',
                selected && 'border-primary',
              )}
              onClick={() => (selection ? selection.onToggle(artifact) : onOpen?.(artifact))}
              data-artifact-key={artifact.id}
              onFocus={() => selection?.onFocus(artifact)}
              onMouseEnter={() => selection?.onFocus(artifact)}
              type="button"
            >
              <WhenVisible
                className={cn(
                  'pointer-events-none overflow-hidden border-b bg-background',
                  size === 'compact' ? 'h-20' : 'h-36 max-sm:h-24',
                )}
              >
                <ArtifactView artifact={artifact} container="preview" />
              </WhenVisible>
              {selected && (
                <span className="absolute top-2 right-2 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-3" />
                </span>
              )}
              <span
                className={cn(
                  'truncate font-medium',
                  size === 'compact' ? 'px-2 py-1.5 text-xs' : 'px-3 pt-2 text-sm',
                )}
              >
                {artifact.title}
              </span>
              <span
                className={cn(
                  'truncate px-3 pb-2 text-muted-foreground text-xs',
                  size === 'compact' && 'hidden',
                )}
              >
                {/* A template says what it makes; another artifact, what it is. */}
                {artifact.template && artifact.description ? (
                  artifact.description
                ) : (
                  <>
                    {artifact.typeTitle} · {artifact.pluginTitle}
                    {artifact.publishedRevision !== null ? ' · Published' : ''}
                  </>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Whether an artifact matches a free-text filter. */
export function artifactMatches(artifact: ArtifactListingEntry, query: string): boolean {
  const words = query.trim().toLowerCase();
  return (
    !words ||
    [artifact.title, artifact.description, artifact.typeTitle, artifact.pluginTitle, artifact.path]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(words)
  );
}
